import { NavigationTabOption, Permission } from '../@types';
import { BetStatus, ConditionEntityType, PublishStatus, TransactionType, WalletType } from '../@types/api';
import { CommissionFieldConfig } from '../components/organisms/crm/commissionFormConfig';
import {
  AFFILIATE_BONUS_CATEGORY,
  AFFILIATE_BONUS_REWARD_TYPE,
  AFFILIATE_CAMPAIGN_PRODUCT,
  AFFILIATE_CAMPAIGN_STATUS,
  AFFILIATE_CAMPAIGN_TYPE,
  APPROVAL_MODE,
  BETTING_ACTIVITY,
  BONUS_CAMPAIGN_TYPE,
  CAMPAIGN_REWARD_TYPE,
  MARKET_TYPE_USED_FOR,
  WELCOME_BONUS_ELIGIBILITY,
  WELCOME_BONUS_GAME_WEIGHTING_CATEGORY,
  WELCOME_BONUS_STATUS,
  WELCOME_BONUS_SCOPE,
  WELCOME_BONUS_TYPE,
  WELCOME_BONUS_WAGERING_MODE,
  VALIDITY_PERIOD,
  MARKET_TYPE_DISPLAY_TYPE,
  EVENT_TYPE,
  WITHDRAWAL_STATUS,
  REOFFER_ODDS_DISPLAY,
  ODDS_FORMAT,
  SPORT_ICON_VARIANT,
  COMPETITIONS_FLAG_VARIANT,
  TEAM_ASSETS_VARIANT,
} from './enums';
import { USER_ROLES } from './keycloak';
import { FEATURE_FLAG } from './featureFlags';
import { COMMISSION_TYPES } from './commission';

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;

export const DATE_FORMAT = 'DD/MM/YYYY';
export const DATE_TIME_FORMAT = `${DATE_FORMAT} HH:mm`;

export const SPORTS_BETTING_TABS: NavigationTabOption[] = [
  { label: 'Settings', value: 'settings', permission: 'manageBettingSettings' },
  {
    label: 'Offer management',
    value: 'offer-management',
    permission: 'manageOffer',
  },
];

export const BETTING_OFFER_TABS: NavigationTabOption[] = [
  { label: 'Sports', value: 'sports', permission: 'manageBettingSettings' },
  { label: 'Competitions', value: 'competitions', permission: 'manageBettingSettings' },
  { label: 'Tournaments', value: 'tournaments', permission: 'manageBettingSettings' },
  { label: 'Participants', value: 'participants', permission: 'manageBettingSettings' },
  { label: 'Players', value: 'players', permission: 'manageBettingSettings' },
  { label: 'Markets', value: 'markets', permission: 'manageBettingSettings' },
  { label: 'Outcomes', value: 'outcomes', permission: 'manageBettingSettings' },
  { label: 'Publish Events', value: 'publish-events', permission: 'accessPublishEvents' },
  { label: 'Conditions', value: 'conditions', permission: 'accessConditions' },
  { label: 'Market Categories', value: 'market-categories', permission: 'manageBettingSettings' },
  { label: 'Risk Management', value: 'risk-management', permission: 'accessRiskManagement' },
];

export const OFFER_MANAGEMENT_TABS: NavigationTabOption[] = [
  { label: 'Events', value: 'events', permission: 'manageEvents' },
  { label: 'Outrights', value: 'outrights', permission: 'manageEvents' },
  {
    label: 'Results',
    value: 'results',
    permission: 'manageEventsResults',
  },
  {
    label: 'Ticket Monitoring',
    value: 'ticket-monitoring',
    permission: 'accessTicketMonitoring',
  },
  {
    label: 'Ticket Authorization',
    value: 'ticket-authorization',
    permission: 'accessTicketAuthorization',
  },
  {
    label: 'Liability Monitoring',
    value: 'liability-monitoring',
    permission: 'accessLiabilityMonitoring',
  },
];

export const CRM_PAGE_TABS: NavigationTabOption[] = [
  { label: 'Partners', value: 'partners', permission: 'accessPartners' },
  { label: 'Players', value: 'players', permission: 'accessPlayers' },
];

export const AFFILIATE_TABS: NavigationTabOption[] = [
  { label: 'Campaigns', value: 'campaigns', permission: 'accessCampaigns' },
  { label: 'Campaign Reports', value: 'campaign-reports', permission: 'accessCampaigns' },
];

export const CASINO_TABS: NavigationTabOption[] = [
  { label: 'Reports', value: 'reports', permission: 'viewCasinoReports' },
  { label: 'Games', value: 'games', permission: 'manageCasinoGames' },
  { label: 'Bets', value: 'bets', permission: 'viewCasinoBets' },
  { label: 'Providers', value: 'providers', permission: 'manageCasinoProviders' },
  { label: 'Transactions', value: 'transactions', permission: 'viewCasinoTransactions' },
  { label: 'Free Spins', value: 'free-spins', permission: 'defaultAdminRole' },
  { label: 'Users', value: 'users', permission: 'viewCasinoUsers' },
  { label: 'Categories', value: 'categories', permission: 'manageCasinoCategories' },
  { label: 'Marketplace', value: 'marketplace', permission: 'defaultSuperAdminRole' },
];

export const CUSTOMER_WALLET_TABS: NavigationTabOption[] = [
  { label: 'Total Credit', value: 'total-credit' },
  { label: 'Payment Transaction', value: 'payment-transaction' },
  { label: 'Wallet Transaction', value: 'wallet-transaction' },
];

export const MY_WALLET_TABS = [{ label: 'Wallet Transaction', value: 'wallet-transaction' }];

export const USER_MANAGEMENT_TABS = [
  { label: 'Users', value: 'users' },
  { label: 'Groups', value: 'groups' },
];

export const LS_KEYS = {
  visibleColumns: 'visibleColumns',
  appFormFilter: 'appFormFilter',
};

export const QUERY_KEYS = {
  sports: 'sports',
  competitions: 'competitions',
  tournaments: 'tournaments',
  participants: 'participants',
  players: 'players',
  marketTypes: 'market-types',
  bonusMarketTypeExclusions: 'bonus-market-type-exclusions',
  bonusMarketTypeExclusionSelectors: 'bonus-market-type-exclusion-selectors',
  outcomeTypes: 'outcome-types',
  scoringUnits: 'scoring-units',
  events: 'events',
  customers: 'customer/search',
  markets: 'markets',
  eventParts: 'event-parts',
  results: 'events/results',
  eventPartScores: 'event-part-scores',
  conditions: 'conditions',
  conditionTypes: 'condition-types',
  conditionGroups: 'condition-groups',
  conditionTemplates: 'condition-templates',
  marketTypeGroups: 'market-type-groups',
  marketTypeGroupCategories: 'market-type-group-categories',
  marketGroups: 'market-groups',
  bettingSlips: 'betting-slips',
  bettingSlipsAnalytics: 'betting-slips-analytics',
  specialValues: 'special-values',
  agentCredentials: 'agent-credentials',
  crmNotes: 'notes',
  crmTasks: 'tasks',
  agents: 'agent',
  locationData: 'location',
  liabilities: 'risk-management/liabilities',
  userProductLiabilities: 'user-product-liabilities',
  riskFactors: 'risk-management/risk-factors',
  liabilitySettings: 'liability-settings',
  riskFactorDefaults: 'risk-factor-defaults',
  crmUsers: 'crm-users',
  crmCommissions: 'crm-commissions',
  riskSports: 'risk-management/sports',
  riskTournaments: 'risk-management/tournaments',
  riskMarkets: 'risk-management/market-types',
  credits: 'credits',
  userBalance: 'balance',
  paymentTransactions: 'transactions/payments',
  walletTransactions: 'transactions/wallets',
  adminTransactions: 'transactions',
  messages: 'messages',
  sentMessages: 'messages/sent',
  deletedMessages: 'messages/deleted',
  messagesCount: 'messagesCount',
  partners: 'partners',
  agentSearch: 'agent/search',
  event: 'event',
  punter: 'punter',
  dailyReports: 'reports/daily',
  riskPlayers: 'risk-management/players',
  agentsReportDetails: 'agents',
  jackpots: 'jackpots',
  stakeRanges: 'jackpots/stake-ranges',
  outrights: 'outrights',
  totalUserCount: 'total-user-count',
  cashout: 'cashout',
  bonus: 'bonus-campaigns',
  cashbackBonus: 'cashback-bonus-campaigns',
  welcomeBonus: 'welcome-bonus',
  wageringConfigs: 'wagering-configs',
  affiliateWelcomeBonusOptions: 'affiliate-welcome-bonus-options',
  userSettings: 'user-settings',
  customerImpersonate: 'customer/impersonation',
  marketplace: 'bo/marketplace',
  marketplaceConfig: 'bo/marketplace/config',
  locales: 'locales',
  depositConfiguration: 'deposit-configuration',
  withdrawalConfiguration: 'withdrawal-configuration',
  approvals: 'payment-requests',
  campaigns: 'campaign',
  cryptoDashboard: 'crypto-dashboard',
  superAdminAccounts: 'super-admin-accounts',
  superAdminFlags: 'super-admin-flags',
  superAdminProviderStatuses: 'super-admin-provider-statuses',
  superAdminActiveTemplate: 'super-admin-active-template',
  casinoGames: 'casino-games',
  casinoProviders: 'casino-providers',
  casinoProvidersList: 'casino-providers-list',
  casinoCategories: 'casino-categories',
  casinoCategoryGames: 'casino-category-games',
  casinoUsers: 'casino-users',
  casinoGoldGamesCampaigns: 'casino-gold-games-campaigns',
  casinoGgaFreeSpins: 'casino-gga-free-spins',
  casinoGgaFreeSpinOptions: 'casino-gga-free-spin-options',
  casinoTransactions: 'casino-transactions',
  casinoBets: 'casino-bets',
  casinoSummary: 'casino-summary',
  casinoRevenueStatistics: 'casino-revenue-statistics',
  casinoUserStatistics: 'casino-user-statistics',
  casinoMarketplace: 'casino-marketplace',
  casinoCurrencies: 'casino-currencies',
  casinoGgaFreeSpinBetLevels: 'casino-gga-free-spin-bet-levels',
} as const;

export const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500];

export const MODEL_TYPES = ['participant', 'player'] as const;

export const MODEL_TYPES_WITH_EVENT = [...MODEL_TYPES, 'event'] as const;

export const MODEL_TYPE_OPTIONS = [
  { id: 'participant', name: 'Participant' },
  { id: 'player', name: 'Player' },
  { id: 'event', name: 'Event' }, // BE stores this as a null, so when modelType is null, we are displaying Event in the UI
];

export const GENDER_ENUM = ['male', 'female', 'other'] as const;

export const GENDER_OPTIONS = [
  { id: 'male', name: 'Male' },
  { id: 'female', name: 'Female' },
  { id: 'other', name: 'Other' },
];

export const CURRENCY_ENUM = ['EUR', 'USD', 'BDT', 'XOF'] as const;

export const CURRENCY_OPTIONS = CURRENCY_ENUM.map((currency) => ({ id: currency, name: currency }));
export const COMPETITIONS_FLAG_VARIANT_OPTIONS = [
  { id: COMPETITIONS_FLAG_VARIANT.DEFAULT, name: 'Default' },
  { id: COMPETITIONS_FLAG_VARIANT.V1, name: 'V1' },
] as const;

export const SPORT_ICON_VARIANT_OPTIONS = [
  { id: SPORT_ICON_VARIANT.COLOR, name: 'Color' },
  { id: SPORT_ICON_VARIANT.FLAT, name: 'Flat' },
  { id: SPORT_ICON_VARIANT.MULTICOLOR, name: 'Multicolor' },
  { id: SPORT_ICON_VARIANT.NEOBET, name: 'Neobet' },
  { id: SPORT_ICON_VARIANT.SIMPLE, name: 'Simple' },
] as const;

export const TEAM_ASSETS_VARIANT_OPTIONS = [
  { id: TEAM_ASSETS_VARIANT.NONE, name: 'None' },
  { id: TEAM_ASSETS_VARIANT.LOGO, name: 'Logo' },
  { id: TEAM_ASSETS_VARIANT.JERSEY, name: 'Jerseys' },
] as const;

export const PUBLISH_STATUS_ICON_COLORS = {
  new: '#AEA9B1',
  ready: '#FEDA2B',
  published: '#78B159',
} as Partial<Record<PublishStatus, string>>;

export const HAS_STARTED_OPTIONS = [
  { id: 1, name: 'Started' },
  { id: 0, name: 'Not started' },
];

export const IS_LIVE_OPTIONS = [
  { id: 1, name: 'In-play' },
  { id: 0, name: 'Pre-match' },
];

export const PUBLISH_STATUS_BASE_OPTIONS = [
  { id: 'new', name: 'New' },
  { id: 'ready', name: 'Ready' },
  { id: 'published', name: 'Published' },
];

export const CHANGED_STATUS_OPTIONS = [
  { id: 1, name: 'Changed' },
  { id: 0, name: 'Not changed' },
];

export const EVENT_SOURCE_OPTIONS = [
  { id: 'manual', name: 'Manual' },
  { id: 'odds_matrix_feed', name: 'Odds Matrix Feed' },
];

export const MATCH_STATUS_OPTIONS = [
  { id: 1, name: 'Enable event' },
  { id: 0, name: 'Disable event' },
];

export const SWITCH_CONTROL_OPTIONS = [
  { id: 1, name: 'Manual' },
  { id: 0, name: 'Automatic' },
];

export const NOTE_TYPES = {
  RISK_MANAGEMENT: 'Risk Management',
  DEPOSITS: 'Deposits',
  WITHDRAWALS: 'Withdrawals',
  PRE_MATCH: 'Pre-Match',
  IN_PLAY: 'In-Play',
  CASINO: 'Casino',
  BONUSES: 'Bonuses',
};

export const NOTE_TYPE_OPTIONS = Object.entries(NOTE_TYPES).map(([id, name]) => ({ id, name }));

export const CUSTOMER_TASK_MESSAGE_TYPES = ['CALL', 'MESSAGE', 'APPOINTMENT'] as const;

export const CUSTOMER_TASK_MESSAGE_TYPE_OPTIONS = [
  { id: 'CALL', name: 'Call' },
  { id: 'MESSAGE', name: 'Message' },
  { id: 'APPOINTMENT', name: 'Appointment' },
];

export const CRM_TASKS_TABS = [
  {
    label: 'Upcoming tasks',
    value: 'upcoming',
  },
  {
    label: 'Past tasks',
    value: 'past',
  },
];

export const MESSAGES_TABS = [
  {
    label: 'Inbox',
    value: 'inbox',
  },
  {
    label: 'Sent',
    value: 'sent',
  },
  {
    label: 'Deleted',
    value: 'deleted',
  },
];

export const BONUS_TABS = [
  {
    label: 'Cashback',
    value: 'cashback',
  },
  {
    label: 'Num. of Selections',
    value: 'selections',
    requireSportsbook: true,
  },
  {
    label: 'Welcome',
    value: 'welcome',
    requireSportsbook: true,
  },
];

export const PAYMENT_SETTINGS_TABS = [
  {
    label: 'Deposit Settings',
    value: 'deposit-settings',
  },
  {
    label: 'Withdrawal Settings',
    value: 'withdrawal-settings',
  },
  {
    label: 'Approvals',
    value: 'approvals',
  },
];

export const SUPER_ADMIN_TABS: NavigationTabOption[] = [
  {
    label: 'Flags',
    value: 'flags',
    permission: 'defaultSuperAdminRole',
  },
  {
    label: 'Actions',
    value: 'actions',
    permission: 'defaultSuperAdminRole',
  },
  {
    label: 'Database Queries',
    value: 'database-queries',
    permission: 'defaultSuperAdminRole',
  },
  {
    label: 'Dashboard',
    value: 'dashboard',
    permission: 'defaultSuperAdminRole',
  },
  {
    label: '0xProcessing',
    value: 'zero-x-processing',
    permission: 'defaultSuperAdminRole',
  },
  {
    label: 'Accounts',
    value: 'accounts',
    permission: 'defaultSuperAdminRole',
  },
];

export const SUPER_ADMIN_DASHBOARD_TABS: NavigationTabOption[] = [
  {
    label: 'Bullboard',
    value: 'bullboard',
    permission: 'defaultSuperAdminRole',
  },
  {
    label: 'FTB Backoffice',
    value: 'ftb-backoffice',
    permission: 'defaultSuperAdminRole',
  },
  {
    label: 'RabbitMQ',
    value: 'rabbitmq',
    permission: 'defaultSuperAdminRole',
  },
  {
    label: 'Zabbix',
    value: 'zabbix',
    permission: 'defaultSuperAdminRole',
  },
];

export const MARKET_STATUS_OPTIONS = [
  { id: 1, name: 'Activate market' },
  { id: 0, name: 'Deactivate market' },
];

export const MARKET_ALL_MATCHES_STATUS_OPTIONS = [
  { id: 1, name: 'Activate market for all matches' },
  { id: 0, name: 'Deactivate market for all matches' },
];

export const OUTCOME_STATUS_OPTIONS = [
  { id: 1, name: 'Enable outcome' },
  { id: 0, name: 'Disable outcome' },
];

export const OUTCOME_ALL_MATCHES_STATUS_OPTIONS = [
  { id: 1, name: 'Enable outcome for all matches' },
  { id: 0, name: 'Disable outcome for all matches' },
];

export const PUBLISH_MODEL_OPTIONS = [
  { id: 'automatic', name: 'Automatic' },
  { id: 'semi_automatic', name: 'Semi-Automatic' },
  { id: 'manual', name: 'Manual' },
];

export const TIME_DELAY_OPTIONS = [
  { id: 0, name: 'Instant' },
  { id: 1, name: '1 Day' },
  { id: 2, name: '2 Days' },
  { id: 3, name: '3 Days' },
  { id: 5, name: '5 Days' },
  { id: 7, name: '7 Days' },
  { id: 10, name: '10 Days' },
  { id: 14, name: '14 Days' },
  { id: 20, name: '20 Days' },
  { id: 30, name: '1 Month' },
  { id: 60, name: '2 Months' },
  { id: 90, name: '3 Months' },
  { id: 180, name: '6 Months' },
  { id: 270, name: '9 Months' },
  { id: 360, name: '1 Year' },
];

export const MODEL_SELECT_FORM_VALUES = [
  {
    name: 'Automatic',
    id: 'automatic',
  },
  {
    name: 'Semi-Automatic',
    id: 'semi_automatic',
  },
  {
    name: 'Manual',
    id: 'manual',
  },
];

export const LANGUAGE_SELECT_VALUES = [
  { name: 'English', id: 'en' },
  { name: 'Serbian', id: 'sr' },
];

export const TRANSFER_STATUS_OPTIONS = [
  { id: 'new', name: 'New' },
  { id: 'ready', name: 'Ready' },
  { id: 'published', name: 'Published' },
];

export const RESOLVE_STATUS_COLORS = {
  open: '#AEA9B1',
  won: '#78B159',
  lost: '#D0021B',
  void: '#947BE7',
  tie: '#0062CC',
  half_won: ['#0062CC', '#78B159'],
  half_lost: ['#0062CC', '#D0021B'],
  deleted: '#FEAA2B',
} as Partial<Record<BetStatus | 'deleted', string | string[]>>;

export const RESOLVE_STATUS_OPTIONS = [
  { id: 'open', name: 'Open' },
  { id: 'won', name: 'Won' },
  { id: 'lost', name: 'Lost' },
  { id: 'void', name: 'Void' },
  { id: 'tie', name: 'Tie' },
  { id: 'half_won', name: 'Half Won' },
  { id: 'half_lost', name: 'Half Lost' },
];

export const VOID_OUTCOME_RESOLVE_STATUS_OPTION = { id: 'void', name: 'Void' };
export const DELETE_OUTCOME_RESOLVE_STATUS_OPTION = { id: 'delete', name: 'Delete' };

export const EVENT_RESULTS_FILTER_OPTIONS = [
  { id: 'with_results', name: 'With results' },
  { id: 'no_results', name: 'Without results' },
  { id: 'incomplete', name: 'Incomplete results' },
];

export const FEATURED_WARNING_MESSAGE =
  'Only one tournament can be set as featured, are you sure that you want to set this one?';

export const CONDITION_ENTITY_SELECT_OPTIONS = [
  { id: 'sport', name: 'Sport' },
  { id: 'competition', name: 'Competition' },
  { id: 'tournament', name: 'Tournament' },
  { id: 'market_type', name: 'Market' },
  { id: 'event', name: 'Event' },
  { id: 'agent', name: 'Agent' },
  { id: 'punter', name: 'Punter' },
];

export const CONDITION_TYPE_ENTITY_SELECT_OPTIONS = [
  ...CONDITION_ENTITY_SELECT_OPTIONS,
  { id: 'ticket', name: 'Ticket' },
];

export const CONDITION_ENTITY_LABELS: Record<ConditionEntityType, string> = {
  sport: 'Sport',
  competition: 'Competition',
  tournament: 'Tournament',
  market_type: 'Market',
  event: 'Event',
  punter: 'Punter',
  ticket: 'Ticket',
  agent: 'Agent',
};

export const CONDITION_NAME_SELECT_OPTIONS = [
  'Max Winning',
  'Max Selections',
  'Max Stake',
  'Max Odds',
  'Max Total Odds',
  'Max Market Selections',
  'Min Ticket Selections',
  'Min Odds Other Selections',
  'Min Stake',
  'Min Odds',
  'Min Total Odds',
  'Min Winning',
  'Min Selections',
  'Exclude Events',
  'Min Ticket Stake',
  'Max Ticket Stake',
  'Max Stake Single',
  'Min Odds Each Selection',
  'Same Tickets Allowed',
  'Same Tickets Allowed Single',
].map((name) => ({ id: name, name }));

export const DISPLAY_SELECT_OPTIONS = [
  {
    name: 'Match',
    id: 'match',
  },
  {
    name: 'Tournament',
    id: 'tournament',
  },
];

export const CONDITIONS_TABS = [
  { label: 'Conditions type', value: 'conditions-type' },
  { label: 'Conditions groups', value: 'conditions-groups' },
  { label: 'Sports', value: 'sports' },
  { label: 'Competitions', value: 'competitions' },
  { label: 'Tournaments', value: 'tournaments' },
  { label: 'Markets', value: 'markets' },
  { label: 'Agents', value: 'agents' },
  { label: 'Punters', value: 'punters' },
  { label: 'Tickets', value: 'tickets' },
];

export const MARKET_CATEGORIES_TABS = [
  { label: 'Market groups', value: 'market-groups' },
  { label: 'Market mix', value: 'market-mix', featureFlag: FEATURE_FLAG.TOURNAMENT_MARKET_GROUPS },
];

export const PARTNERS_REPORT_TABS: NavigationTabOption[] = [
  { label: 'Managers', value: 'managers', permission: 'viewManagersReports', requireSportsbook: true },
  { label: 'Agents', value: 'agents', permission: 'viewAgentsReports', requireSportsbook: true },
  {
    label: 'Affiliates',
    value: 'affiliates',
    permission: 'viewAffiliatesReports',
    marketplaceSystemFlag: 'affiliate',
  },
];

export const CONDITIONS_COMBINATION_MESSAGE = {
  'Max Odds': 'Max Odds and Max Stake Combination',
  'Min Odds': 'Min Odds and Min Stake',
};

export const MARKET_TYPE_USED_FOR_OPTIONS = [
  { id: MARKET_TYPE_USED_FOR.PRE_MATCH, name: 'Pre-match' },
  { id: MARKET_TYPE_USED_FOR.IN_PLAY, name: 'In-play' },
];

export const EVENT_TYPE_OPTIONS = [
  { id: EVENT_TYPE.MATCH, name: 'Match' },
  { id: EVENT_TYPE.OUTRIGHT, name: 'Outright' },
];

export const REFETCH_INTERVAL_OPTIONS = [
  { id: 0, name: 'No refresh' },
  { id: 5 * SECOND, name: '5 seconds' },
  { id: 10 * SECOND, name: '10 seconds' },
  { id: 30 * SECOND, name: '30 seconds' },
  { id: 60 * SECOND, name: '60 seconds' },
  { id: 300 * SECOND, name: '5 minutes' },
];

export const BETTING_SLIP_FROM_OPTIONS = [
  { id: 'Pre-match', name: 'Pre-match' },
  { id: 'In-play', name: 'In-play' },
  { id: 'Mix', name: 'Mix' },
];

export const BETTING_SLIP_TYPE_OPTIONS = [
  { id: 'Single', name: 'Single' },
  { id: 'Multiple', name: 'Multiple' },
  { id: 'System', name: 'System' },
];

export const BETTING_SLIP_STATUS_OPTIONS = [
  { id: 'open', name: 'Open' },
  { id: 'void', name: 'Void' },
  { id: 'won', name: 'Won' },
  { id: 'lost', name: 'Lost' },
  { id: 'cashout', name: 'Cash out', permission: 'manageCashOut' as Permission },
];

export const BETTING_SLIP_ACCEPT_STATUS_OPTIONS = [
  { id: 'pending', name: 'Pending' },
  { id: 'accepted', name: 'Accepted' },
  { id: 'rejected', name: 'Rejected' },
  { id: 'admin_cancelled', name: 'Admin Cancelled' },
];

export const DEVICE_OPTIONS = [
  { id: 'mobile', name: 'Mobile' },
  { id: 'desktop', name: 'Desktop' },
];

export const BETTING_SLIP_IS_WAYS_OPTIONS = [
  { id: 1, name: 'Yes' },
  { id: 0, name: 'No' },
];

export const BETTING_SLIP_BET_BUILDER_OPTIONS = [
  { id: 1, name: 'Yes' },
  { id: 0, name: 'No' },
];

export const BETTING_SLIP_VERIFICATION_OPTIONS = [
  { id: 'manual', name: 'Manual' },
  { id: 'automatic', name: 'Automatic' },
];

export const OPERATOR_COUNTDOWN_DURATION = 300;
export const PUNTER_COUNTDOWN_DURATION = 60;

export const BACKOFFICE_WS_ROOM_ID = 'backoffice.users';
export const BETSLIP_RESOLUTION_WS_EVENT = 'betslip.resolution';

export const RISK_MANAGEMENT_TABS = [
  { label: 'Risk settings', value: 'risk-settings' },
  { label: 'Sports', value: 'sports' },
  { label: 'Tournaments', value: 'tournaments' },
  { label: 'Markets', value: 'markets' },
  { label: 'Players', value: 'players' },
];

export const PRODUCT_TYPE_OPTIONS = [
  { id: 'pre-match', name: 'Pre-match' },
  { id: 'in-play', name: 'In-play' },
];

export const RISK_FACTOR_ENTITIES = ['sport', 'tournament', 'market_type', 'punter', 'global'] as const;

export const RISK_FACTOR_ENTITY_OPTIONS = [
  { id: 'sport', name: 'Sport' },
  { id: 'tournament', name: 'Tournament' },
  { id: 'market_type', name: 'Market' },
  { id: 'punter', name: 'Punter' },
] as const;

export const AGENT_COMMISSION_TYPE_OPTIONS = [
  {
    id: COMMISSION_TYPES.POSITION,
    name: 'Position',
  },
  {
    id: COMMISSION_TYPES.PROFIT,
    name: 'Profit',
  },
];

export const AFFILIATE_COMMISSION_TYPE_OPTIONS = [
  // {
  //   id: 'REVENUE_SHARE',
  //   name: 'Revenue share',
  // },
  {
    id: COMMISSION_TYPES.CPA,
    name: 'Cost per acquisition',
  },
  // {
  //   id: 'HYBRID',
  //   name: 'Hybrid',
  // },
];

export const PLAYER_COMMISSION_TYPE_OPTIONS = [
  ...AGENT_COMMISSION_TYPE_OPTIONS,
  {
    id: COMMISSION_TYPES.PER_PICK,
    name: 'Per pick',
  },
];

export const NESTED_PLAYER_COMMISSION_FIELDS: Partial<Record<string, CommissionFieldConfig[]>> = {
  [COMMISSION_TYPES.PER_PICK]: [
    {
      name: 'picks',
      label: 'Per Pick Commission',
      type: 'picks-table',
      gridSize: 12,
    },
  ],
  [COMMISSION_TYPES.POSITION]: [
    {
      name: 'playerCommissionPercentage',
      label: '%',
      type: 'number',
      gridSize: 2.5,
    },
  ],
  [COMMISSION_TYPES.PROFIT]: [
    {
      name: 'playerCommissionPercentage',
      label: '%',
      type: 'number',
      gridSize: 2.5,
    },
  ],
};

export const CURRENCY = {
  code: 'EUR',
  symbol: '€',
};

export const PREDEFINED_DATE_RANGE_OPTIONS = [
  { id: 'custom', name: 'Custom' },
  { id: 'today', name: 'Today' },
  { id: 'yesterday', name: 'Yesterday' },
  { id: 'this_week', name: 'This Week' },
  { id: 'last_week', name: 'Last Week' },
  { id: 'last_7_days', name: 'Last 7 Days' },
  { id: 'last_30_days', name: 'Last 30 Days' },
  { id: 'this_month', name: 'This Month' },
  { id: 'last_month', name: 'Last Month' },
  { id: 'last_3_months', name: 'Last 3 Months' },
  { id: 'last_6_months', name: 'Last 6 Months' },
  { id: 'last_12_months', name: 'Last 12 Months' },
] as const;

export const TRANSACTION_TYPE_OPTIONS: Record<WalletType, { id: TransactionType; name: string }[]> = {
  main: [
    { id: 'deposit', name: 'Deposit' },
    { id: 'withdrawal', name: 'Withdrawal' },
    { id: 'other', name: 'Other' },
  ],
  bonus: [{ id: 'bonus', name: 'Bonus' }],
};

export const WALLET_TYPE_OPTIONS = (bonusWalletDisabled: boolean) => [
  {
    id: 'main',
    name: 'Main',
  },
  {
    id: 'bonus',
    name: 'Bonus',
    disabled: bonusWalletDisabled,
  },
];

export const WALLET_TRANSACTION_TYPE_OPTIONS = [
  { id: 'deposit', name: 'Deposit' },
  { id: 'withdrawal', name: 'Withdrawal' },
  { id: 'transfer', name: 'Transfer' },
  { id: 'bonus', name: 'Bonus' },
  { id: 'other', name: 'Other' },
  { id: 'cashout', name: 'Cashout', permission: 'manageCashOut' as Permission },
  { id: 'place_bet', name: 'Place Bet' },
  { id: 'won_bet', name: 'Won Bet' },
  { id: 'won_to_lost_bet', name: 'Won to Lost Bet' },
];

export const PAYMENT_TRANSACTION_TYPE_OPTIONS = WALLET_TRANSACTION_TYPE_OPTIONS.slice(0, 5);

export const PAYMENT_METHOD_FILTER_OPTIONS = [
  { id: 'ALL', name: 'All' },
  { id: 'Agent', name: 'Agent' },
  { id: 'CRYPTO', name: 'Crypto' },
];

export const PRODUCT_REPORTS_TABS: NavigationTabOption[] = [
  { label: 'Daily', value: 'daily', permission: 'viewDailyReports', requireSportsbook: true },
  { label: 'Sportsbook', value: 'sportsbook', permission: 'viewSportsbookReports', requireSportsbook: true },
  {
    label: 'Partners',
    value: 'partners',
    permission: 'viewPartnersReports',
    standaloneCasinoSystemFlag: 'affiliate',
  },
  { label: 'Players', value: 'players', permission: 'viewPlayersReports', requireSportsbook: true },
  { label: 'Payments', value: 'payments', permission: 'viewPaymentReports', requireSportsbook: true },
  { label: 'Bonus', value: 'bonus', permission: 'viewBonusReports', requireSportsbook: true },
  {
    label: 'Jackpot',
    value: 'jackpot',
    permission: 'accessReportsJackpot',
    standaloneCasinoConfigFlag: 'jackpotEnabled',
  },
  { label: 'Cashout', value: 'cashout', permission: 'viewCashoutReports', requireSportsbook: true },
];

export const SPORTSBOOK_TABS = [
  { label: 'Sports', value: 'sports' },
  { label: 'Tournaments', value: 'tournaments' },
  { label: 'Markets', value: 'markets' },
];

export const BETTING_SLIP_REOFFERED_OPTIONS = [
  { id: 1, name: 'Yes' },
  { id: 0, name: 'No' },
];

export const BETTING_SLIP_AUTHORIZED_TICKETS_OPTIONS = [
  { id: 'reoffered', name: 'Reoffered' },
  { id: 'verified', name: 'Verified' },
];

export const CREATE_MESSAGE_USERS_OPTIONS_MAP = {
  Administrator: [
    {
      id: 'Agents',
      name: 'Agents',
    },
    {
      id: 'Managers',
      name: 'Managers',
    },
    {
      id: 'Players',
      name: 'Players',
    },
    {
      id: 'Support',
      name: 'Support',
    },
  ],
  Manager: [
    {
      id: 'Agents',
      name: 'Agents',
    },
    {
      id: 'Players',
      name: 'Players',
    },
  ],
  Agent: [
    {
      id: 'Players',
      name: 'Players',
    },
  ],
  Affiliate: [],
  Trader: [],
  'Technical Support': [],
  'Super Administrator': [],
};

export const ODDS_FORMAT_OPTIONS = [
  { id: ODDS_FORMAT.DECIMAL, name: 'Decimal' },
  { id: ODDS_FORMAT.FRACTIONAL, name: 'Fractional' },
  { id: ODDS_FORMAT.AMERICAN, name: 'American' },
];

export const REOFFER_ODDS_DISPLAY_OPTIONS = [
  { id: REOFFER_ODDS_DISPLAY.ALL, name: 'All' },
  { id: REOFFER_ODDS_DISPLAY.NEW, name: 'New' },
];

export const SPORTSBOOK_SOURCE_OPTIONS = [
  { id: 'desktop', name: 'Desktop' },
  { id: 'mobile', name: 'Mobile' },
];

export const SPORTSBOOK_WALLET_TYPE = [
  { id: 'main', name: 'Real Money Bets' },
  { id: 'bonus', name: 'Credit Money Bets' },
];

export const SPORTSBOOK_FROM_OPTIONS = [
  { id: 'Pre-match', name: 'Pre-match' },
  { id: 'In-play', name: 'In-play' },
  { id: 'Mix', name: 'Mix' },
];

export const PAYMENTS_TABS: NavigationTabOption[] = [
  { label: 'Total Payments', value: 'total', permission: 'viewPaymentReports' },
  { label: 'Payment List', value: 'list', permission: 'viewPaymentReports' },
];

export const PAYMENT_STATUS_OPTIONS = [
  { id: 'completed', name: 'Completed' },
  { id: 'canceled', name: 'Canceled' },
];

export const FILTER_LIABILITY_BETTING_OFFER = [
  { id: 'pre-match', name: 'Pre-match' },
  { id: 'in-play', name: 'In-play' },
];

export const BETSLIP_CONTROL_DELAY_OPTIONS = [
  { id: 0, name: 'None' },
  { id: 1, name: '1 Sec' },
  { id: 3, name: '3 Sec' },
  { id: 5, name: '5 Sec' },
  { id: 7, name: '7 Sec' },
  { id: 10, name: '10 Sec' },
];

export const BETTING_TYPES = [
  'Single',
  'Double',
  'Treble',
  '4 Fold',
  '5 Fold',
  '6 Fold',
  '7 Fold',
  '8 Fold',
  '9 Fold',
  '10 Fold',
  '11 Fold',
  '12 Fold',
  '13 Fold',
  '14 Fold',
  '15 Fold',
  '16 Fold',
  '17 Fold',
  '18 Fold',
  '19 Fold',
  '20 Fold',
];

export const MARKET_TYPE_TYPE_OPTIONS = [
  {
    id: 'match',
    name: 'Match',
  },
  {
    id: 'outright',
    name: 'Outright',
  },
  {
    id: 'match_and_outright',
    name: 'Match & Outright',
  },
];

export const MARKET_TYPE_EXCLUSION_TYPE_OPTIONS = MARKET_TYPE_TYPE_OPTIONS.filter(
  ({ id }) => id !== 'match_and_outright'
);

export const MARKET_TYPE_DISPLAY_TYPE_OPTIONS = [
  {
    id: MARKET_TYPE_DISPLAY_TYPE.SINGLE,
    name: 'Single',
  },
  {
    id: MARKET_TYPE_DISPLAY_TYPE.MULTIPLE,
    name: 'Multiple',
  },
  {
    id: MARKET_TYPE_DISPLAY_TYPE.TEAM,
    name: 'Team',
  },
  {
    id: MARKET_TYPE_DISPLAY_TYPE.GROUPED,
    name: 'Grouped',
  },
];

export const COMMISSION_PICKS = ['1', '2', '3', '4-7', '8-9', '10+'] as const;

export const CASHOUT_TYPE_OPTIONS = [
  { id: 'full', name: 'Full Cash Out' },
  { id: 'partial', name: 'Partial Cash Out' },
];

export const BONUS_PRODUCT_TYPE_OPTIONS = [{ id: 'SPORTSBOOK', name: 'Sportsbook' }] as const;

export const CASHBACK_BONUS_PRODUCT_TYPE_OPTIONS = [
  { id: 'SPORTSBOOK', name: 'Sportsbook' },
  { id: 'CASINO', name: 'Casino' },
] as const;

export const CASHBACK_BONUS_REWARD_TYPE_OPTIONS = [
  { id: CAMPAIGN_REWARD_TYPE.REAL_MONEY, name: 'Real Money' },
  { id: CAMPAIGN_REWARD_TYPE.FREE_SPINS, name: 'Free Spins' },
] as const;

export const WELCOME_BONUS_PRODUCT_TYPE_OPTIONS = [
  { id: AFFILIATE_CAMPAIGN_PRODUCT.SPORTSBOOK, name: 'Sportsbook' },
  { id: AFFILIATE_CAMPAIGN_PRODUCT.CASINO, name: 'Casino' },
] as const;

export const WELCOME_BONUS_ELIGIBILITY_OPTIONS = [
  { id: WELCOME_BONUS_ELIGIBILITY.FIRST_TIME_DEPOSIT, name: 'First Time Deposit' },
] as const;

export const WELCOME_BONUS_TYPE_OPTIONS = [
  { id: WELCOME_BONUS_TYPE.UNLOCK, name: 'Unlock' },
  { id: WELCOME_BONUS_TYPE.UPFRONT, name: 'Upfront (TBD)', disabled: true },
] as const;

export const WELCOME_BONUS_VALIDITY_PERIOD_OPTIONS = [{ id: VALIDITY_PERIOD.ONGOING, name: 'Ongoing' }] as const;

export const WELCOME_BONUS_STATUS_OPTIONS = [
  { id: WELCOME_BONUS_STATUS.READY, name: 'Ready' },
  { id: WELCOME_BONUS_STATUS.IN_PROGRESS, name: 'In Progress' },
  { id: WELCOME_BONUS_STATUS.FINISHED, name: 'Finished' },
] as const;

export const WELCOME_BONUS_SCOPE_OPTIONS = [
  { id: WELCOME_BONUS_SCOPE.GLOBAL, name: 'Global' },
  { id: WELCOME_BONUS_SCOPE.SPECIAL, name: 'Special' },
] as const;

type WelcomeBonusStatusOptionId = (typeof WELCOME_BONUS_STATUS_OPTIONS)[number]['id'];

export const WELCOME_BONUS_VALID_TRANSITIONS: Record<
  WelcomeBonusStatusOptionId,
  readonly WelcomeBonusStatusOptionId[]
> = {
  [WELCOME_BONUS_STATUS.READY]: [WELCOME_BONUS_STATUS.IN_PROGRESS, WELCOME_BONUS_STATUS.FINISHED],
  [WELCOME_BONUS_STATUS.IN_PROGRESS]: [WELCOME_BONUS_STATUS.FINISHED],
  [WELCOME_BONUS_STATUS.FINISHED]: [],
};

export const isValidWelcomeBonusStatusTransition = (
  status: WelcomeBonusStatusOptionId,
  newStatus: WelcomeBonusStatusOptionId
) => WELCOME_BONUS_VALID_TRANSITIONS[status]?.includes(newStatus) ?? false;

export const getWelcomeBonusStatusOptions = (status?: WelcomeBonusStatusOptionId | null) => {
  if (!status) return WELCOME_BONUS_STATUS_OPTIONS;

  return WELCOME_BONUS_STATUS_OPTIONS.filter(
    (option) => option.id === status || isValidWelcomeBonusStatusTransition(status, option.id)
  ).map((option) => (option.id === status ? { ...option, disabled: true } : option));
};

export const WELCOME_BONUS_WAGERING_MODE_OPTIONS = [
  { id: WELCOME_BONUS_WAGERING_MODE.FIXED_AMOUNT, name: 'Fixed amount' },
  { id: WELCOME_BONUS_WAGERING_MODE.MULTIPLIER, name: 'Multiplier X' },
] as const;

export const WELCOME_BONUS_SPORTSBOOK_REWARD_TYPE_OPTIONS = [
  { id: CAMPAIGN_REWARD_TYPE.BONUS_CREDITS, name: 'Bonus Credits' },
  { id: CAMPAIGN_REWARD_TYPE.REAL_MONEY, name: 'Real Money' },
] as const;

export const WELCOME_BONUS_CASINO_REWARD_TYPE_OPTIONS = [
  { id: CAMPAIGN_REWARD_TYPE.REAL_MONEY, name: 'Real Money' },
] as const;

export const WELCOME_BONUS_REWARD_TYPE_OPTIONS = [...WELCOME_BONUS_SPORTSBOOK_REWARD_TYPE_OPTIONS] as const;

export const WELCOME_BONUS_GAME_WEIGHTING_OPTIONS = [
  { id: WELCOME_BONUS_GAME_WEIGHTING_CATEGORY.SLOTS, name: 'Slots' },
  { id: WELCOME_BONUS_GAME_WEIGHTING_CATEGORY.ROULETTE, name: 'Roulette' },
  { id: WELCOME_BONUS_GAME_WEIGHTING_CATEGORY.BLACKJACK, name: 'Blackjack' },
  { id: WELCOME_BONUS_GAME_WEIGHTING_CATEGORY.LIVE_CASINO, name: 'Live Casino' },
] as const;

export const BONUS_ELIGIBILITY_OPTIONS = [
  { id: 'LOST_MONEY_IN_DEFINED_TIMEFRAME', name: 'Lost money in defined time frame' },
] as const;

export const CAMPAIGN_BONUS_TYPE_OPTIONS = [{ id: 'FIRST_TIME_DEPOSIT', name: 'First Time Deposit' }] as const;

export const CAMPAIGN_REWARD_TYPE_OPTIONS = [
  { id: CAMPAIGN_REWARD_TYPE.BONUS_CREDITS, name: 'Bonus Credits' },
  { id: CAMPAIGN_REWARD_TYPE.REAL_MONEY, name: 'Real Money' },
  { id: CAMPAIGN_REWARD_TYPE.FREE_SPINS, name: 'Free Spins' },
] as const;

export const AFFILIATE_BONUS_REWARD_TYPE_OPTIONS = [
  { id: AFFILIATE_BONUS_REWARD_TYPE.BONUS_CREDITS, name: 'Bonus Credits' },
  { id: AFFILIATE_BONUS_REWARD_TYPE.REAL_MONEY, name: 'Real Money' },
] as const;

export const AFFILIATE_BONUS_CATEGORY_OPTIONS = [
  { id: AFFILIATE_BONUS_CATEGORY.PERCENTAGE, name: 'Percentage' },
  { id: AFFILIATE_BONUS_CATEGORY.FIXED_AMOUNT, name: 'Fixed Amount' },
] as const;

export const SELECTION_BONUS_ELIGIBILITY_OPTIONS = [
  { id: 'PRE_MATCH', name: 'Pre-match' },
  { id: 'IN_PLAY', name: 'In-play' },
  { id: 'MIXED', name: 'Mixed' },
];

export const BONUS_TIMEFRAME_OPTIONS = [
  { id: 'DAILY', name: 'Daily' },
  { id: 'WEEKLY', name: 'Weekly' },
  { id: 'MONTHLY', name: 'Monthly' },
] as const;

export const VALIDITY_PERIOD_OPTIONS = [
  {
    id: VALIDITY_PERIOD.CUSTOM,
    name: 'Custom',
  },
  {
    id: VALIDITY_PERIOD.ONGOING,
    name: 'Ongoing',
  },
] as const;

export const BONUS_STATUS_OPTIONS_CUSTOM = [
  { id: 'SCHEDULED', name: 'Scheduled' },
  { id: 'IN_PROGRESS', name: 'In Progress', disabled: true },
  { id: 'CANCELLED', name: 'Cancelled' },
  { id: 'FINISHED', name: 'Finished', disabled: true },
] as const;

export const BONUS_STATUS_OPTIONS_ONGOING = [
  { id: 'READY', name: 'Ready' },
  { id: 'IN_PROGRESS', name: 'In Progress' },
  { id: 'FINISHED', name: 'Finished' },
] as const;

export const CASHBACK_BONUS_STATUS_FILTER_OPTIONS = [
  { id: 'READY', name: 'Ready' },
  { id: 'SCHEDULED', name: 'Scheduled' },
  { id: 'IN_PROGRESS', name: 'In Progress' },
  { id: 'CANCELLED', name: 'Cancelled' },
  { id: 'FINISHED', name: 'Finished' },
] as const;

export const CONTROL_OPTIONS = [
  {
    id: 0,
    name: 'Reset to Auto',
  },
];

export const BONUS_CAMPAIGN_TYPE_OPTIONS = [
  { id: BONUS_CAMPAIGN_TYPE.CASHBACK, name: 'Cashback' },
  { id: BONUS_CAMPAIGN_TYPE.NUM_OF_SELECTIONS, name: 'Num. of selections' },
  { id: BONUS_CAMPAIGN_TYPE.WELCOME_BONUS, name: 'Welcome Bonus' },
];

export const WITHDRAWAL_APPROVAL_MODE_OPTIONS = [
  { id: APPROVAL_MODE.AUTO_REJECT, name: 'Auto Reject' },
  { id: APPROVAL_MODE.MANUAL_VERIFY, name: 'Manual Verify' },
  { id: APPROVAL_MODE.VERIFY_ALL, name: 'Verify All' },
  { id: APPROVAL_MODE.NONE, name: 'None' },
];

export const DEPOSIT_APPROVAL_MODE_OPTIONS = [
  { id: APPROVAL_MODE.AUTO_REJECT, name: 'Auto Reject' },
  { id: APPROVAL_MODE.NONE, name: 'None' },
];

export const BETTING_ACTIVITY_OPTIONS = [
  { id: BETTING_ACTIVITY.ALL, name: 'All' },
  { id: BETTING_ACTIVITY.ACTIVE, name: 'Active' },
  { id: BETTING_ACTIVITY.INACTIVE, name: 'Inactive' },
];

export const AFFILIATE_CAMPAIGN_STATUS_OPTIONS = [
  { id: AFFILIATE_CAMPAIGN_STATUS.READY, name: 'Ready' },
  { id: AFFILIATE_CAMPAIGN_STATUS.IN_PROGRESS, name: 'In Progress' },
  { id: AFFILIATE_CAMPAIGN_STATUS.PAUSED, name: 'Paused' },
  { id: AFFILIATE_CAMPAIGN_STATUS.FINISHED, name: 'Finished' },
];

export const AFFILIATE_CAMPAIGN_TYPE_OPTIONS = [
  { id: AFFILIATE_CAMPAIGN_TYPE.BANNER, name: 'Banner' },
  { id: AFFILIATE_CAMPAIGN_TYPE.SOCIAL_MEDIA, name: 'Social Media' },
  { id: AFFILIATE_CAMPAIGN_TYPE.EMAIL, name: 'Email' },
  { id: AFFILIATE_CAMPAIGN_TYPE.INFLUENCER, name: 'Influencer' },
  { id: AFFILIATE_CAMPAIGN_TYPE.OTHER, name: 'Other' },
];

export const AFFILIATE_CAMPAIGN_PRODUCT_OPTIONS = [
  { id: AFFILIATE_CAMPAIGN_PRODUCT.SPORTSBOOK, name: 'Sportsbook' },
  { id: AFFILIATE_CAMPAIGN_PRODUCT.CASINO, name: 'Casino' },
  { id: AFFILIATE_CAMPAIGN_PRODUCT.ALL, name: 'All' },
];

export const WITHDRAWAL_STATUS_OPTIONS = [
  { id: WITHDRAWAL_STATUS.REQUESTED, name: 'Requested' },
  { id: WITHDRAWAL_STATUS.PENDING, name: 'Pending' },
  { id: WITHDRAWAL_STATUS.APPROVED, name: 'Approved' },
  { id: WITHDRAWAL_STATUS.REJECTED, name: 'Rejected' },
];

export const PARTNER_TYPE_OPTIONS = [
  { id: USER_ROLES.AGENT, name: 'Agents' },
  { id: USER_ROLES.AFFILIATE, name: 'Affiliates' },
];
